import { HttpParams } from '@angular/common/http';
import { Component, inject, OnInit } from '@angular/core';
import {
  NavigationEnd,
  NavigationStart,
  Router,
  RouterOutlet,
} from '@angular/router';
import { AuthorizationService } from '@modules/authorization/services/authorization.service';
import {
  GetProcessCode,
  ProceedRegistration,
  StartRegistrationNewUser,
} from '@modules/authorization/store/authorization.action';
import { LoadDocuments } from '@modules/documents/store/documents.actions';
import { AppState } from '@modules/shared/store';
import {
  GetConfirmServiceList,
  HideSpinnerLoading,
  ShowSpinnerLoading,
} from '@modules/shared/store/common.actions';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { StorageKeys } from '@modules/storage/storage.enum';
import { Storage } from '@modules/storage/storage.service';
import { CookieService } from './shared/components/cookie/cookie.service';
import { MainRoutes } from './config/routes.config';
import { LocalStorageEnum } from './shared/local-storage.enum';
import { AsyncPipe } from '@angular/common';
import { CookieComponent } from './shared/components/cookie/cookie.component';
import { ChatBotComponent } from '@modules/shared/components/chat-bot/chat-bot.component';
import { PdfJsViewerModule } from 'ng2-pdfjs-viewer';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [CookieService],
  imports: [
    RouterOutlet,
    CookieComponent,
    AsyncPipe,
    ChatBotComponent,
    PdfJsViewerModule,
  ],
})
export class AppComponent implements OnInit {
  public isShowLoader$: Observable<boolean>;
  private store = inject<Store<AppState>>(Store);
  private cookie = inject(CookieService);
  public isCookieAccepted$: Observable<boolean> = this.cookie.isCookieAccepted;
  private router = inject(Router);
  private authService = inject(AuthorizationService);
  private storage = inject(Storage);
  private order: string | null;

  constructor() {
    this.isShowLoader$ = this.store.select(state => state.common.isShowLoader);
    this.handleRouteNavigation();
  }

  public get isAuthorized(): boolean {
    return this.authService.isAuthorized();
  }

  public ngOnInit(): void {
    localStorage.removeItem('pdfjs.history');

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).pdfWorkerSrc = 'assets/pdf.worker.min.mjs';
    this.initApp();
  }

  private handleRouteNavigation(): void {
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationStart => event instanceof NavigationStart
        ),
        map((event: NavigationStart) => event.url),
        take(1)
      )
      .subscribe(this.saveRouteToLocalStorage);

    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        ),
        map((event: NavigationEnd) => event.url),
        filter(url => this.isRouteInLocalStorage(url)),
        take(1)
      )
      .subscribe(this.removeRouteFromLocalStorage);
  }

  private saveRouteToLocalStorage = (url: string): void => {
    const segments = url.split('/');
    const mainRoute = segments[1];

    const needSave = !(
      mainRoute === MainRoutes.ERROR ||
      mainRoute === MainRoutes.AUTH ||
      mainRoute === MainRoutes.OAUTH
    );

    if (needSave) {
      localStorage.setItem('route', url);
    }
  };

  private isRouteInLocalStorage = (url: string): boolean => {
    const savedRoute = localStorage.getItem('route');
    return savedRoute && savedRoute === url;
  };

  private removeRouteFromLocalStorage = (): void => {
    localStorage.removeItem('route');
  };

  private initApp(): void {
    const url = new URL(window.location.href);
    const isOrderParam = url.searchParams.get('order');

    if (isOrderParam) {
      localStorage.setItem('order', isOrderParam);
      this.removeRouteFromLocalStorage();
    }

    if (url.pathname === '/') {
      const queryBot: string = url.searchParams.get(StorageKeys.Bot);
      if (queryBot) {
        this.storage.setItem(StorageKeys.Bot, queryBot);
      }
    }

    const isAuthorized =
      localStorage.getItem(LocalStorageEnum.isAuthorized) === 'true';

    if (
      isAuthorized &&
      (window.location.pathname === '/' ||
        window.location.href.includes('?order='))
    ) {
      this.createProposalByLink();
    }

    if (isAuthorized) {
      this.store.dispatch(new GetConfirmServiceList());
    }

    const processId = localStorage.getItem(LocalStorageEnum.processId);
    const href = window.location.href;
    const hasOrderParam = href.includes('?order=');
    const hasOrderNumber = href.includes('orderNumber');
    const hasErrorOrCancel = href.includes('error') || href.includes('cancel');

    if (!hasOrderParam && !hasOrderNumber && !hasErrorOrCancel) {
      if (isAuthorized) {
        this.store.dispatch(new LoadDocuments());
        this.store.dispatch(new ShowSpinnerLoading());
      }
      if (processId) {
        this.store.dispatch(new GetProcessCode());
      } else {
        if (href.includes('?')) {
          const httpParams = new HttpParams({
            fromString: href.split('?')[1],
          });
          const stateUrl = httpParams.get('state');
          const code = httpParams.get('code');
          localStorage.setItem('code', code);
          const state = localStorage.getItem('authState');
          if (stateUrl === state && stateUrl && state) {
            this.router.navigate(['/oauth2']);
            this.store.dispatch(new ProceedRegistration());
          }
        }
      }
      this.store.dispatch(new HideSpinnerLoading());
    }

    this.store.dispatch(new HideSpinnerLoading());

    if (
      localStorage.getItem(LocalStorageEnum.isAuthorized) !== 'true' &&
      !localStorage.getItem('code') &&
      !localStorage.getItem('authState')
    ) {
      localStorage.setItem(LocalStorageEnum.isAuthorized, 'false');
      this.store.dispatch(new StartRegistrationNewUser());
    }
  }

  private createProposalByLink(): void {
    this.order = window.location.href.includes('?order=')
      ? window.location.href.split('?order=')[1]
      : null;
    localStorage.setItem('order', this.order);
  }
}
